import { PsHttpService } from '@patsnap/tahiti-core/common/ps-http';
import type {
    PsHttp,
    PatSnapSuccessResponseData,
} from '@patsnap/tahiti-core/common/ps-http';
import { injectable } from '@patsnap/tahiti-core/shared/inversify';
import { getCoreTelecomApiUrlPrefix } from '../../common/constants';
import type { NoveltyCheckApi } from './novelty-check-api';
import type {
    CreateNoveltyCheckTaskRequest,
    CreateNoveltyCheckTaskResponse,
    DetectLanguageRequest,
    DetectLanguageResponse,
    TranslateTextRequest,
    TranslateTextResponse,
    Pic2TextRequest,
    Pic2TextResponse,
    BatchUploadImagesRequest,
    BatchUploadImagesResponse,
} from './types';
import {
    DEFAULT_IMAGE_FOLDER_PATH,
    DEFAULT_IMAGE_FILE_PREFIX,
} from './constants';

import type { PsHttpChannel } from '@patsnap/tahiti-core';
/**
 * 新颖性检索 API 实现
 * @description 实现所有新颖性检索相关的 API 调用
 */
@injectable()
export class NoveltyCheckApiImpl extends PsHttpService implements NoveltyCheckApi {
    private _coreTelecomApiUrlPrefix = ''
    private _initPromise: Promise<void>

    constructor(httpClient: PsHttp, httpChannelClient: PsHttpChannel) {
        super(httpClient, httpChannelClient)
        this._initPromise = this.init()
    }

    private async init() {
        this._coreTelecomApiUrlPrefix = await getCoreTelecomApiUrlPrefix()
    }

    /**
     * 确保初始化完成
     */
    private async ensureInitialized() {
        await this._initPromise
    }

    get options() {
        return {
            baseURL: this._coreTelecomApiUrlPrefix,
            headers: {
                'Content-Type': 'application/json',
            },
        };
    }

    /**
     * 创建查新任务
     */
    async createNoveltyCheckTask(data: CreateNoveltyCheckTaskRequest) {
        await this.ensureInitialized();
        const res = await this.post<PatSnapSuccessResponseData<CreateNoveltyCheckTaskResponse>>({
            url: '/novelty-search/submit',
            data,
        });
        return res.data;
    }

    /**
     * 检测输入文本的语言
     */
    async detectLanguage(data: DetectLanguageRequest) {
        await this.ensureInitialized();
        const res = await this.post<PatSnapSuccessResponseData<DetectLanguageResponse>>({
            url: '/ai/task/lang-detect',
            data,
        });
        return res.data;
    }

    /**
     * 翻译文本
     */
    async translateText(data: TranslateTextRequest) {
        await this.ensureInitialized();
        const res = await this.post<PatSnapSuccessResponseData<TranslateTextResponse>>({
            url: '/translation/text',
            data,
        });
        return res.data;
    }

    /**
     * 图片转文本
     */
    async pic2Text(data: Pic2TextRequest) {
        await this.ensureInitialized();
        const res = await this.post<PatSnapSuccessResponseData<Pic2TextResponse>>({
            url: '/novelty-search/novelty_search_pic2t',
            data,
        });
        return res.data;
    }

    /**
     * 批量上传图片
     */
    async batchUploadImages(request: BatchUploadImagesRequest): Promise<BatchUploadImagesResponse> {
        await this.ensureInitialized();
        const formData = new FormData();

        // 添加文件列表
        for (const file of request.files) {
            formData.append('files', file);
        }

        // 添加可选参数
        formData.append('folder_path', request.folder_path ?? DEFAULT_IMAGE_FOLDER_PATH);
        formData.append('file_prefix', request.file_prefix ?? DEFAULT_IMAGE_FILE_PREFIX);
        formData.append('generate_unique_name', String(request.generate_unique_name ?? true));

        const result = await this.post<PatSnapSuccessResponseData<BatchUploadImagesResponse>>({
            url: '/image-upload/batch',
            data: formData,
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });

        return result.data;
    }
}
